import { apiFetch } from "@/lib/api";

export interface DeviceLookupSerialFci {
  id: number;
  deviceLabelSerial: string | null;
  finishedSku: string | null;
  firmwareVersion: string | null;
  pcbaSerial: string | null;
  clampSerial: string | null;
  boxSerial: string | null;
  dateAssembled: string | null;
  note: string | null;
  mcuUuid: string | null;
  imei: string | null;
  iccid: string | null;
  childPcbaSerial: string | null;
  scrap: boolean;
  touchSample: boolean;
  rebuild: boolean;
  reasonNotInBatch: string | null;
  offBatchItemLabel: string | null;
  stationId: number | null;
  stationName: string | null;
  locationId: number | null;
  locationName: string | null;
  buildOrderId: number | null;
  buildOrderNumber: string | null;
  buildOrderSku: string | null;
  pcbaBuildOrderId: number | null;
  tlaBuildOrderId: number | null;
  batchItemId: number | null;
  batchItemSku: string | null;
  batchItemQuantity: number | null;
  batchId: number | null;
  batchMfrRemark: string | null;
  shipmentId: number | null;
  shipmentSentToPackOut: boolean | null;
  salesOrderId: number | null;
  salesOrderNumber: string | null;
  cartonNumber: number | null;
}

export interface DeviceLookupPotting {
  id: number;
  deviceLabelSerial: string | null;
  clampSerial: string | null;
  pottingGrade: string | null;
  sentAt: string | null;
  notes: string | null;
  gradedAt: string | null;
  gradedBy: string | null;
  pass: boolean | null;
  purchaseOrderItemId: number | null;
  purchaseOrderNumber: string | null;
  purchaseOrderMvId: number | null;
  purchaseOrderMvStatus: string | null;
  purchaseOrderItemSku: string | null;
  purchaseOrderItemQuantity: number | null;
}

export interface DeviceLookupClampQc {
  id: number;
  clampSerial: string | null;
  sku: string | null;
  purchaseOrderNumber: string | null;
  sgsQcAt: string | null;
  sgsQcBy: string | null;
  sgsQcWorkOrderId: number | null;
  sgsMechanicalPass: boolean | null;
  sgsInductancePass: boolean | null;
  sgsLineHarvestPerformed: boolean | null;
  sgsLineHarvestPass: boolean | null;
  sgsLineHarvestDrag: number | null;
}

export interface DeviceLookupPcbaCalibration {
  id: number;
  pcbaSerial: string | null;
  calibratedAt: string | null;
  sequenceSet: string | null;
}

export type DeviceLookupProductionTests = import("./calTestApi").ProductionTestDeviceStatus;

export interface DeviceLookupLaborScan {
  id: number;
  scannedAt: string;
  stationPage: string;
  stationLabel: string;
  userEmail: string | null;
  sessionStartedAt: string;
  sessionEndedAt: string | null;
  allocatedSeconds: number | null;
  resolutionPath: string;
}

export interface DeviceLookupWirelessCheckin {
  deviceLabelSerial: string;
  addedAt: string | null;
  hit: string | null;
  pass: boolean | null;
  decidedAt: string | null;
  decidedByEmail: string | null;
  completedAt: string | null;
  completionOutcome: string | null;
  isActive: boolean;
}

export interface DeviceLookupFinalQcVisualInspection {
  pass: boolean;
  decidedAt: string | null;
  decidedByEmail: string | null;
}

export interface DeviceLookupQualityIncident {
  id: number;
  type: string | null;
  issue: string | null;
  issueDate: string | null;
  issuePerson: string | null;
  repairScrap: string | null;
  resolutionDate: string | null;
  resolutionPerson: string | null;
  rootCause: string | null;
  status: "resolved" | "unresolved";
}

export interface DeviceLookupPottingRework {
  id: number;
  incidentId: number;
  routedAt: string | null;
  routedBy: string | null;
  sentAt: string | null;
  regradedAt: string | null;
  regradePottingGrade: string | null;
  regradeNotes: string | null;
  purchaseOrderId: number | null;
  purchaseOrderItemId: number | null;
  purchaseOrderNumber: string | null;
  purchaseOrderMvId: number | null;
}

export interface DeviceLookupPcbaTest {
  id: number;
  mcuId: string | null;
  powerTestPass: boolean | null;
  firmwarePass: boolean | null;
  ban: boolean | null;
  banReason: string | null;
  firmwareVersion: string | null;
  firmwareFile: string | null;
  firmwareTimestamp: string | null;
  powerTestTimestamp: string | null;
}

export interface DeviceLookupManufacturingState {
  headline: string;
  detail: string;
  missingItems: string[];
}

export interface DeviceLookupEpisode {
  id: number;
  deviceId: number;
  deviceLabelSerial: string | null;
  finishedSku: string | null;
  firmwareVersion: string | null;
  imei: string | null;
  mcuUuid: string | null;
  pcbaSerial: string | null;
  clampSerial: string | null;
  batchItemId: number | null;
  stationId: number | null;
  stationName: string | null;
  archivedAt: string | null;
  dateAssembled: string | null;
  rebuild: boolean;
  active: boolean;
}

export interface DeviceLookupTouchSample {
  touchSampleAt: string | null;
  touchSampleMarkedBy: string | null;
  touchSampleNotes: string | null;
  touchSampleRecoveredAt: string | null;
  touchSampleRecoveredBy: string | null;
}

export interface DeviceLookupReport {
  querySerial: string;
  deviceId: number;
  selectedSerialFciId: number;
  activeSerialFciId: number | null;
  episodes: DeviceLookupEpisode[];
  serialFci: DeviceLookupSerialFci;
  touchSample: DeviceLookupTouchSample;
  manufacturingState: DeviceLookupManufacturingState;
  pottingRows: DeviceLookupPotting[];
  clampQc: DeviceLookupClampQc | null;
  pcbaCalibrations: DeviceLookupPcbaCalibration[];
  productionTests: DeviceLookupProductionTests | null;
  laborScans: DeviceLookupLaborScan[];
  wirelessCheckin: DeviceLookupWirelessCheckin | null;
  finalQcVisualInspection: DeviceLookupFinalQcVisualInspection | null;
  qualityIncidents: DeviceLookupQualityIncident[];
  pottingReworkRows: DeviceLookupPottingRework[];
  pcbaTestRows: DeviceLookupPcbaTest[];
  pcbaTestRequirementSkipped: boolean;
}

export const DEVICE_LOOKUP_QUERY_KEY = "manufacturing-device-lookup";

export function fetchDeviceLookupReport(
  serial: string,
  options?: { serialFciId?: number | null },
): Promise<DeviceLookupReport> {
  const params = new URLSearchParams({ serial: serial.trim() });
  if (options?.serialFciId != null) {
    params.set("serialFciId", String(options.serialFciId));
  }
  return apiFetch<DeviceLookupReport>(`/api/manufacturing/device-lookup?${params.toString()}`);
}
